"use client";

import React, { useState, useEffect, useMemo } from "react";
import { FieldRenderer } from "@/components/molecules/fieldRenderer";
import { useToast } from "@/hooks/useToast";
import { FIELD_NAMES, ACTION_TYPES } from "@/constants/admin/common";
import { DEFAULT_ADMIN_FILE_INPUT_ACCEPT } from "@/constants/fileUpload";

// Constants
const EMPTY_DATA: Record<string, any> = {};

// Types
interface FieldConfig {
  fieldName: string;
  label?: string;
  value?: any;
  valueType?: string;
  filedType: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  containerClassName?: string;
  rows?: number;
  options?: Array<{ value: string; label: string }>;
  iconName?: string;
  color?: string;
  width?: string;
  height?: string;
  title?: string;
  description?: string;
  children?: React.ReactNode;
  text?: string;
  variant?: string;
  required?: boolean;
  suppressToast?: boolean;
  /**
   * Optional conditional visibility. When set, the field is only rendered (and
   * validated) while another field's current value matches. Fields WITHOUT this
   * prop always render, so existing widgets are unaffected.
   *   showWhen: { field: "design_variant", equals: "variant_2" }
   *   showWhen: { field: "design_variant", in: ["variant_1", "variant_3"] }
   */
  showWhen?: { field: string; equals?: any; in?: any[] };
  createMethod?: (data: any) => Promise<any>;
  updateMethod?: (data: any) => Promise<any>;
  dispatchMethod?: (data: any) => void;
  validation?: (value: any, formData: any) => string | null;
  [key: string]: any;
}

interface FieldsFormatterProps {
  data?: Record<string, any>;
  fields?: FieldConfig[];
  onSubmit?: (data: any) => void;
  className?: string;
  updateParent?: boolean;
  onChange?: (data: any) => void;
  validationErrors?: Record<string, boolean>;
  validationMessages?: Record<string, string>;
  separateButton?: boolean;
  /** Applied to FILE fields without an explicit `accept` (e.g. video + images + PDF). */
  fileAccept?: string;
}

// Helpers
const EMPTY_VALUES = [null, undefined, ""];

  const isFieldEmpty = (field: FieldConfig, value: any): boolean => {
    if (field.filedType === "TEXT_BLOCK") return false;
    if (!field.required) return false; // Not required = not considered "empty" for validation

    return EMPTY_VALUES.includes(value) ||
    (typeof value === "string" && value.trim() === "");
};

const initializeFormData = (
  fields: FieldConfig[],
  data: Record<string, any>
): Record<string, any> => {
  const formData = fields.reduce((acc: Record<string, any>, field: FieldConfig) => {
    if (field.fieldName === FIELD_NAMES.SUBMIT_BUTTON) return acc;
    if (field.filedType === "TEXT_BLOCK") return acc;
    acc[field.fieldName] = data?.[field.fieldName] ?? field.value;

    const raw = data?.[field.fieldName];
    const hasKey =
      data &&
      typeof data === "object" &&
      Object.prototype.hasOwnProperty.call(data, field.fieldName);

    if (field.filedType === "FILE" && !field.required) {
      if (hasKey) {
        acc[field.fieldName] = raw;
      } else {
        // No API value: omit key so Redux merge does not send "" and wipe persisted URL
      }
    } else {
      let stored = raw ?? field.value;
      if (
        field.filedType === "SELECT" &&
        stored != null &&
        stored !== ""
      ) {
        stored = String(stored);
      }
      acc[field.fieldName] = stored;
    }
    return acc;
  }, {});

  if (data?.id) {
    formData.id = data.id;
  }

  return formData;
};

// Component
const FieldsFormatter: React.FC<FieldsFormatterProps> = ({
  data,
  fields = [],
  onSubmit,
  className,
  updateParent = false,
  onChange,
  validationErrors: parentValidationErrors = {},
  validationMessages: parentValidationMessages = {},
  separateButton = false,
  fileAccept = DEFAULT_ADMIN_FILE_INPUT_ACCEPT,
}) => {
  const stableData = data ?? EMPTY_DATA;

  const fieldsWithFileAccept = useMemo(() => {
    if (!fileAccept) return fields;
    return fields.map((f) =>
      f.filedType === "FILE" && f.accept == null
        ? { ...f, accept: fileAccept }
        : f
    );
  }, [fields, fileAccept]);

  const [formData, setFormData] = useState<Record<string, any>>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, boolean>>({});
  const [validationMessages, setValidationMessages] = useState<Record<string, string>>({});

  const { toast } = useToast();

  // Create a stable key to detect when data content actually changes
  // This prevents infinite loops by only triggering when actual field values change
  const dataKey = React.useMemo(() => {
    if (!stableData || Object.keys(stableData).length === 0) return 'empty';
    const relevantValues = fieldsWithFileAccept
      .filter(f => f.fieldName !== FIELD_NAMES.SUBMIT_BUTTON)
      .map(f => `${f.fieldName}:${stableData[f.fieldName] ?? ''}`)
      .join('|');
    return `${stableData.id ?? 'no-id'}_${relevantValues}`;
  }, [stableData, fieldsWithFileAccept]);

  // Sync local form when parent `data` meaningfully changes. Depend only on `dataKey` — including
  // `stableData` / `fieldsWithFileAccept` by reference caused infinite loops (new object/array each
  // parent render → effect → setFormData → repeat) and "Maximum update depth exceeded".
  useEffect(() => {
    setFormData(initializeFormData(fieldsWithFileAccept, stableData));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fields + data are folded into dataKey
  }, [dataKey]);

  // Event Handlers
  const handleChange = (fieldName: string, value: any): void => {
    const updatedFormData = { ...formData, [fieldName]: value };
    setFormData(updatedFormData);

    if (updateParent && onChange) {
      onChange(updatedFormData);
    }

    if (validationErrors[fieldName] || parentValidationErrors[fieldName]) {
      setValidationErrors((prev) => ({ ...prev, [fieldName]: false }));
      setValidationMessages((prev) => ({ ...prev, [fieldName]: "" }));
    }
  };

  // Conditional visibility — a field with `showWhen` only renders/validates while
  // the referenced field's current value matches. Fields without `showWhen` are
  // always visible, so existing widgets are completely unaffected.
  const isFieldVisible = (field: FieldConfig): boolean => {
    const cond = field.showWhen;
    if (!cond?.field) return true;
    const current = formData[cond.field];
    if (cond.equals !== undefined) return String(current) === String(cond.equals);
    if (Array.isArray(cond.in)) return cond.in.map(String).includes(String(current));
    return true;
  };

  const validateForm = (): boolean => {
    const errors: Record<string, boolean> = {};
    const messages: Record<string, string> = {};
    let isValid = true;

    fieldsWithFileAccept.forEach((field) => {
      if (field.fieldName === FIELD_NAMES.SUBMIT_BUTTON) return;
      if (field.filedType === "TEXT_BLOCK") return;
      // Skip fields hidden by their showWhen condition (can't block save).
      if (!isFieldVisible(field)) return;

      const fieldValue = formData[field.fieldName];

      // Check custom validation first
      if (field.validation) {
        const validationError = field.validation(fieldValue, formData);
        if (validationError) {
          errors[field.fieldName] = true;
          messages[field.fieldName] = validationError;
          isValid = false;
        }
      } else if (field.required && isFieldEmpty(field, fieldValue)) {
        errors[field.fieldName] = true;
        messages[field.fieldName] = "This field is required";
        isValid = false;
      }
    });

    setValidationErrors(errors);
    setValidationMessages(messages);
    return isValid;
  };

  const handleAction = async (field: FieldConfig): Promise<void> => {
    if (!validateForm()) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsLoading(true);

      const { createMethod, updateMethod, dispatchMethod, suppressToast } = field;
      const isUpdate = Boolean(formData?.id && updateMethod);

      // Action mapping based on operation type
      const actionMap = {
        [ACTION_TYPES.UPDATE]: {
          method: updateMethod,
          message: "Data updated successfully",
        },
        [ACTION_TYPES.CREATE]: {
          method: createMethod,
          message: "Data created successfully",
        },
      };

      const actionType = isUpdate ? ACTION_TYPES.UPDATE : ACTION_TYPES.CREATE;
      const action = actionMap[actionType];

      let result: any;

      if (action.method) {
        result = await action.method(formData);
        if (!suppressToast) {
          toast({
            title: "Success",
            description: action.message,
            variant: "success",
          });
        }
      }

      if (dispatchMethod && result) {
        dispatchMethod(result);
      }

      // Callback mapping based on parent update mode
      const callbackMap = {
        true: () => onChange?.(formData),
        false: () => onSubmit?.(result || formData),
      };

      callbackMap[String(updateParent) as "true" | "false"]();
    } catch (error) {
      console.error("❌ Operation failed:", error);
      toast({
        title: "Error",
        description: "Operation failed",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Merged validation state
  const mergedValidationErrors = { ...validationErrors, ...parentValidationErrors };
  const mergedValidationMessages = { ...validationMessages, ...parentValidationMessages };

  const visibleFields = fieldsWithFileAccept.filter(isFieldVisible);

  // Render helpers
  const renderField = (field: FieldConfig, index: number) => (
    <div
      key={field.fieldName || `field-${index}`}
      className={field.containerClassName || "w-full"}
    >
      {FieldRenderer(
        field,
        formData,
        handleChange,
        handleAction,
        isLoading,
        toast,
        mergedValidationErrors,
        mergedValidationMessages[field.fieldName]
      )}
    </div>
  );

  // Render with separated button
  if (separateButton) {
    const regularFields = visibleFields.filter((f) => f.fieldName !== FIELD_NAMES.SUBMIT_BUTTON);
    const submitButton = visibleFields.find((f) => f.fieldName === FIELD_NAMES.SUBMIT_BUTTON);

    return (
      <div className={`flex h-full min-h-0 w-full min-w-0 flex-col bg-white p-6 ${className || ""}`}>
        <div className="min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto px-6 pt-4">
          <form
            className="flex max-w-full min-w-0 flex-wrap items-start gap-4"
            onSubmit={(e: React.FormEvent) => e.preventDefault()}
          >
            {regularFields.map(renderField)}
          </form>
        </div>

        {submitButton && (
          <div className="min-w-0 shrink-0 border-t border-gray-200 bg-white px-6 py-3">
            {FieldRenderer(
              submitButton,
              formData,
              handleChange,
              handleAction,
              isLoading,
              toast,
              mergedValidationErrors,
              mergedValidationMessages[submitButton.fieldName]
            )}
          </div>
        )}
      </div>
    );
  }

  // Default render
  return (
    <div className={`w-full h-full p-6 bg-white ${className || ""}`}>
      <form
        className="flex flex-wrap items-start gap-4"
        onSubmit={(e: React.FormEvent) => e.preventDefault()}
      >
        {visibleFields.map(renderField)}
      </form>
    </div>
  );
};

export default FieldsFormatter;
